import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ModalityCapabilityV1,
  QuaternionSignalModalityV1,
  RecordingCapabilitiesV1,
  SignalCategoricalSeriesV1,
  SignalMinMaxSeriesV1,
  SignalNativeSeriesV1,
  SignalOverviewSeriesV1,
  SignalOverviewV1,
  SignalProfileV1,
  SignalWindowSeriesV1,
  SignalWindowV1,
} from "@rekonas/domain";
import { Alert, Spinner } from "@rekonas/ui";
import {
  createDefaultCameraViews,
  linkCameraViews,
  updateCameraViews,
  type CameraView,
} from "./camera";
import { ORIENTATION_MODELS } from "./config";
import {
  applyViewerArrowKey,
  clampSelectedTime,
  detailWindowStart,
  hasNativeArrowKeyBehavior,
  INITIAL_TIME_WINDOW,
  isActiveTimelineDrag,
  timelineSecondsAtClientX,
  type TimeWindow,
} from "./interaction";
import { OrientationPanel } from "./OrientationPanel";
import { orientationAtTime, type OrientationSelection } from "./orientation";
import {
  loadViewerPreferences,
  saveViewerPreferences,
  type ViewerPreferences,
} from "./preferences";

export const MAX_SIGNAL_WINDOWS = 5;
type Layer = "both" | "recorded" | "derived";
type PlotSeries = SignalOverviewSeriesV1 | SignalWindowSeriesV1;

export interface ViewerDataSource {
  key: readonly unknown[];
  recordingRef: string;
  durationSeconds: number;
  profile: SignalProfileV1;
  loadCapabilities(signal: AbortSignal): Promise<RecordingCapabilitiesV1>;
  loadOverview(modalities: string[], maxPoints: number, signal: AbortSignal): Promise<SignalOverviewV1>;
  loadWindow(
    modalities: string[],
    startSeconds: number,
    durationSeconds: 15 | 30 | 60,
    signal: AbortSignal,
  ): Promise<SignalWindowV1>;
}

export function isCurrentSignalWindow(
  result: SignalWindowV1,
  expected: {
    recordingRef: string;
    dataVersion: string;
    startSeconds: number;
    durationSeconds: 15 | 30 | 60;
  },
): boolean {
  return result.recording_ref === expected.recordingRef &&
    result.data_version === expected.dataVersion &&
    result.requested.start_seconds === expected.startSeconds &&
    result.requested.duration_seconds === expected.durationSeconds;
}

export function trimSignalWindowCache(
  client: ReturnType<typeof useQueryClient>,
  scope: readonly unknown[],
  maximum = MAX_SIGNAL_WINDOWS,
): void {
  const windows = client.getQueryCache().findAll({ queryKey: [...scope, "window"] })
    .sort((left, right) => right.getObserversCount() - left.getObserversCount() || right.state.dataUpdatedAt - left.state.dataUpdatedAt);
  windows.slice(maximum).forEach((query) => client.removeQueries({ queryKey: query.queryKey, exact: true }));
}

export function formatRecordingTime(seconds: number, includeSeconds = false): string {
  const safe = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return includeSeconds
    ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function separatedChannelLabel(modalityLabel: string, channelLabel: string, index: number): string {
  const [context, modalityName] = modalityLabel.split("·").map((part) => part.trim());
  const humanChannel = channelLabel
    .replace(/^[a-z]+\d+[_.\s-]+/i, "")
    .replaceAll(/[_-]+/g, " ")
    .replace(/([a-z])([0-9])/gi, "$1 $2")
    .replaceAll(/\s+/g, " ")
    .trim();
  const base = modalityName || context;
  const distinctChannel = humanChannel.localeCompare(base, undefined, { sensitivity: "accent" }) === 0
    ? `${humanChannel} ${index + 1}`
    : humanChannel;
  return modalityName ? `${context} · ${distinctChannel}` : distinctChannel;
}

function capabilityMessage(capability: ModalityCapabilityV1 | undefined, modalityId: string): string {
  if (modalityId === "ambient_light") return "Not recorded";
  if (modalityId === "swa") return "Not computed";
  if (modalityId === "position") return "Unavailable";
  if (!capability) return "Capability unavailable";
  if (capability.state === "not_recorded") return "Not recorded";
  if (capability.state === "not_computed") return "Not computed";
  if (capability.state === "unsupported_mapping") return "Unsupported mapping";
  if (capability.state === "temporarily_unavailable") return "Temporarily unavailable";
  if (capability.state === "queued" || capability.state === "processing") return "Processing";
  if (capability.state === "failed") return modalityId === "sleep_stage" ? "Sleep analysis failed" : "Unavailable";
  return "No samples in this interval";
}

function SignalCanvas(props: {
  interval: { start_seconds: number; end_seconds: number };
  series: Array<SignalNativeSeriesV1 | SignalMinMaxSeriesV1>;
  onSeek(seconds: number): void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = () => {
      const bounds = canvas.getBoundingClientRect();
      const width = Math.max(320, Math.floor(bounds.width || 900));
      const height = Math.max(56, Math.floor(bounds.height || 68));
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.strokeStyle = "rgba(14, 36, 70, 0.10)";
      context.lineWidth = 1;
      for (let index = 1; index < 6; index += 1) {
        const x = (width * index) / 6;
        context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke();
      }
      let minimum = Number.POSITIVE_INFINITY;
      let maximum = Number.NEGATIVE_INFINITY;
      const include = (value: number | null) => {
        if (value == null || !Number.isFinite(value)) return;
        minimum = Math.min(minimum, value); maximum = Math.max(maximum, value);
      };
      props.series.forEach((item) => item.representation === "native"
        ? item.values.forEach(include)
        : item.bins.forEach((bin) => { include(bin.minimum); include(bin.maximum); }));
      if (!Number.isFinite(minimum)) minimum = -1;
      if (!Number.isFinite(maximum)) maximum = 1;
      const span = Math.max(maximum - minimum, 1e-12);
      const xAt = (seconds: number) => ((seconds - props.interval.start_seconds) /
        (props.interval.end_seconds - props.interval.start_seconds)) * width;
      const yAt = (value: number) => height - 9 - ((value - minimum) / span) * (height - 18);
      const colors = ["#15395f", "#139a8a", "#697f96", "#a57b16"];
      props.series.forEach((item, seriesIndex) => {
        context.strokeStyle = colors[seriesIndex % colors.length];
        context.lineWidth = seriesIndex === 0 ? 1.3 : 1;
        if (item.representation === "minmax") {
          item.bins.forEach((bin) => {
            if (bin.minimum == null || bin.maximum == null) return;
            const x = xAt((bin.start_seconds + bin.end_seconds) / 2);
            context.beginPath(); context.moveTo(x, yAt(bin.minimum)); context.lineTo(x, yAt(bin.maximum)); context.stroke();
          });
          return;
        }
        context.beginPath();
        let drawing = false;
        item.values.forEach((value, index) => {
          if (value == null || !Number.isFinite(value)) { drawing = false; return; }
          const x = xAt(item.start_seconds + index * item.sample_period_seconds);
          const y = yAt(value);
          if (!drawing) context.moveTo(x, y); else context.lineTo(x, y);
          drawing = true;
        });
        context.stroke();
      });
    };
    draw();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(draw);
    observer?.observe(canvas);
    return () => observer?.disconnect();
  }, [props.interval, props.series]);

  function seek(event: MouseEvent<HTMLCanvasElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    props.onSeek(props.interval.start_seconds + ratio * (props.interval.end_seconds - props.interval.start_seconds));
  }
  return <canvas aria-label="Signal plot; click to seek" className="signal-row-canvas" onClick={seek} ref={canvasRef} role="img" />;
}

export function sleepStageClass(label: string): string {
  const normalized = label.trim().toLowerCase().replaceAll(/[^a-z0-9]+/g, "-");
  return ["wake", "n1", "n2", "n3", "rem"].includes(normalized) ? `sleep-${normalized}` : "sleep-unknown";
}

export function isActiveSleepStage(
  highlight: boolean,
  modalityId: string,
  selectedSeconds: number,
  interval: { start_seconds: number; end_seconds: number },
): boolean {
  return highlight && modalityId === "sleep_stage" && selectedSeconds >= interval.start_seconds && selectedSeconds < interval.end_seconds;
}

function CategoricalTrack(props: {
  highlightSelected: boolean;
  interval: { start_seconds: number; end_seconds: number };
  selectedSeconds: number;
  series: SignalCategoricalSeriesV1;
  onSeek(seconds: number): void;
}) {
  const duration = props.interval.end_seconds - props.interval.start_seconds;
  return (
    <button
      aria-label={`${props.series.modality_label} intervals; click to seek`}
      className="categorical-track"
      onClick={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
        props.onSeek(props.interval.start_seconds + ratio * duration);
      }}
      type="button"
    >
      {props.series.intervals.map((item) => {
        const left = ((item.start_seconds - props.interval.start_seconds) / duration) * 100;
        const width = ((item.end_seconds - item.start_seconds) / duration) * 100;
        const label = props.series.labels[item.code] || item.code;
        const selected = isActiveSleepStage(props.highlightSelected, props.series.modality_id, props.selectedSeconds, item);
        return (
          <span
            className={`category-segment ${sleepStageClass(label)}${selected ? " category-selected" : ""}`}
            key={`${item.start_seconds}-${item.code}`}
            style={{ left: `${left}%`, width: `${width}%` }}
            title={`${label}: ${formatRecordingTime(item.start_seconds, true)}`}
          >
            {width > 5 ? label : ""}
          </span>
        );
      })}
    </button>
  );
}

function TimeAxis({ interval }: { interval: { start_seconds: number; end_seconds: number } }) {
  return <div className="signal-time-axis">{Array.from({ length: 7 }, (_, index) => {
    const seconds = interval.start_seconds + ((interval.end_seconds - interval.start_seconds) * index) / 6;
    return <span key={index}>{formatRecordingTime(seconds, interval.end_seconds - interval.start_seconds <= 60)}</span>;
  })}</div>;
}

function TrackRow(props: {
  capability: ModalityCapabilityV1 | undefined;
  highlightActiveStage: boolean;
  interval: { start_seconds: number; end_seconds: number };
  loading: boolean;
  label: string;
  modality: SignalProfileV1["modalities"][number];
  requestError: boolean;
  selectedSeconds: number;
  series: PlotSeries[];
  onSeek(seconds: number): void;
}) {
  const numerical = props.series.filter(
    (item): item is SignalNativeSeriesV1 | SignalMinMaxSeriesV1 => item.representation !== "intervals",
  );
  const categorical = props.series.find(
    (item): item is SignalCategoricalSeriesV1 => item.representation === "intervals",
  );
  const status = props.requestError ? "Request error"
    : props.loading && props.capability?.state === "available" ? "Loading track…"
      : capabilityMessage(props.capability, props.modality.id);
  return (
    <div className={`signal-track-row state-${props.capability?.state || "unknown"}`}>
      <div className="signal-track-label">
        <strong>{props.label}</strong>
        <span>{props.modality.origin}</span>
        <small>{props.capability?.channels.length
          ? `${props.capability.channels.length > 1 ? `${props.capability.channels.length} ch · ` : ""}${props.capability.channels[0].unit}`
          : props.modality.kind === "media" ? "media" : "status"}</small>
      </div>
      <div className="signal-track-plot">
        {categorical ? <CategoricalTrack highlightSelected={props.highlightActiveStage} interval={props.interval} onSeek={props.onSeek} selectedSeconds={props.selectedSeconds} series={categorical} />
          : numerical.length ? <SignalCanvas interval={props.interval} onSeek={props.onSeek} series={numerical} />
            : <div className="track-status"><span>{status}</span></div>}
      </div>
    </div>
  );
}

function FullNightTimeline(props: {
  duration: number;
  selected: number;
  detailStart: number;
  detailDuration: number | null;
  onCommit(seconds: number): void;
  onPreview(seconds: number | null): void;
}) {
  const dragging = useRef<number | null>(null);
  const timelineDuration = Math.max(props.duration, 1e-9);

  function secondsAt(event: ReactPointerEvent<HTMLDivElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return timelineSecondsAtClientX(event.clientX, bounds.left, bounds.width, props.duration);
  }

  return (
    <div
      aria-label="Full-night timeline scrubber"
      aria-valuemax={props.duration}
      aria-valuemin={0}
      aria-valuenow={props.selected}
      className="full-night-timeline"
      onLostPointerCapture={(event) => {
        if (dragging.current === event.pointerId) {
          dragging.current = null;
          props.onPreview(null);
        }
      }}
      onPointerCancel={(event) => {
        if (dragging.current === event.pointerId) {
          dragging.current = null;
          props.onPreview(null);
        }
      }}
      onPointerDown={(event) => {
        dragging.current = event.pointerId;
        event.currentTarget.setPointerCapture(event.pointerId);
        props.onPreview(secondsAt(event));
      }}
      onPointerMove={(event) => {
        if (isActiveTimelineDrag(dragging.current, event.pointerId, event.currentTarget.hasPointerCapture(event.pointerId))) {
          props.onPreview(secondsAt(event));
        }
      }}
      onPointerUp={(event) => {
        if (dragging.current !== event.pointerId) return;
        const seconds = secondsAt(event);
        dragging.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        props.onCommit(seconds);
      }}
      role="slider"
      tabIndex={0}
    >
      {props.detailDuration != null && <i className="detail-window-range" style={{
        left: `${(props.detailStart / timelineDuration) * 100}%`,
        width: `${Math.max(0.2, (props.detailDuration / timelineDuration) * 100)}%`,
      }} />}
      <i className="full-night-cursor" data-testid="full-night-cursor" style={{ left: `${(props.selected / timelineDuration) * 100}%` }} />
    </div>
  );
}

export function SignalViewer(props: {
  dataSource: ViewerDataSource;
  summary?: ReactNode;
  provenance?: ReactNode;
  toolbar?: ReactNode;
  qualityLabel?: string;
  attention?: string | null;
}) {
  const client = useQueryClient();
  const [layer, setLayer] = useState<Layer>("both");
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(INITIAL_TIME_WINDOW);
  const [selectedSeconds, setSelectedSeconds] = useState(0);
  const [scrubberPreviewSeconds, setScrubberPreviewSeconds] = useState<number | null>(null);
  const [hoverSeconds, setHoverSeconds] = useState<number | null>(null);
  const [preferences, setPreferences] = useState<ViewerPreferences>(loadViewerPreferences);
  const [cameraViews, setCameraViews] = useState(createDefaultCameraViews);
  const lastCameraSource = useRef<"head" | "torso">("head");
  const hoverFrame = useRef<number | null>(null);
  const settingsMenu = useRef<HTMLDetailsElement>(null);
  const identity = useMemo(() => props.dataSource.key.join("|"), [props.dataSource.key]);
  const priorIdentity = useRef(identity);
  useEffect(() => {
    if (priorIdentity.current !== identity) {
      void client.cancelQueries({ queryKey: props.dataSource.key });
      setLayer("both"); setTimeWindow(INITIAL_TIME_WINDOW); setSelectedSeconds(0); setHoverSeconds(null); setScrubberPreviewSeconds(null);
      setCameraViews(createDefaultCameraViews()); lastCameraSource.current = "head";
      priorIdentity.current = identity;
    }
  }, [client, identity, props.dataSource.key]);
  useEffect(() => saveViewerPreferences(preferences), [preferences]);
  useEffect(() => {
    if (!preferences.cursorFollowsMouse) setHoverSeconds(null);
  }, [preferences.cursorFollowsMouse]);
  useEffect(() => () => {
    if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
  }, []);

  const capabilities = useQuery({
    queryKey: [...props.dataSource.key, "capabilities"],
    queryFn: ({ signal }) => props.dataSource.loadCapabilities(signal),
  });
  const requestedModalities = useMemo(() => props.dataSource.profile.modalities
    .filter((item) => item.kind !== "quaternion" && item.kind !== "media" && item.kind !== "unavailable")
    .filter((item) => layer === "both" || item.origin === layer)
    .map((item) => item.id), [layer, props.dataSource.profile]);
  const overview = useQuery({
    queryKey: [...props.dataSource.key, "overview", requestedModalities.slice().sort().join(","), 8000],
    queryFn: ({ signal }) => props.dataSource.loadOverview(requestedModalities, 8_000, signal),
    enabled: Boolean(capabilities.data),
    staleTime: 60_000,
  });
  const duration: 15 | 30 | 60 = timeWindow === "full" ? 30 : timeWindow;
  const windowStart = detailWindowStart(selectedSeconds, duration, props.dataSource.durationSeconds);
  const detail = useQuery({
    queryKey: [...props.dataSource.key, "window", requestedModalities.slice().sort().join(","), windowStart, duration],
    queryFn: async ({ signal }) => {
      const result = await props.dataSource.loadWindow(requestedModalities, windowStart, duration, signal);
      if (!isCurrentSignalWindow(result, {
        recordingRef: props.dataSource.recordingRef,
        dataVersion: capabilities.data?.data_version || "", startSeconds: windowStart, durationSeconds: duration,
      })) throw new DOMException("Obsolete signal response", "AbortError");
      return result;
    },
    enabled: timeWindow !== "full" && Boolean(capabilities.data && requestedModalities.length),
    gcTime: 5 * 60_000,
    staleTime: 30_000,
  });
  const orientationModalities = props.dataSource.profile.modalities.filter(
    (item): item is QuaternionSignalModalityV1 => item.kind === "quaternion" && ORIENTATION_MODELS.some((model) => model.modalityId === item.id),
  );
  const orientationStart = Math.max(0, Math.min(Math.max(0, props.dataSource.durationSeconds - 15), selectedSeconds - 7.5));
  const orientation = useQuery({
    queryKey: [...props.dataSource.key, "window", "orientation", orientationStart, orientationModalities.map((item) => item.id).join(",")],
    queryFn: ({ signal }) => props.dataSource.loadWindow(orientationModalities.map((item) => item.id), orientationStart, 15, signal),
    enabled: Boolean(capabilities.data && orientationModalities.some((modality) =>
      capabilities.data?.modalities.some((item) => item.id === modality.id && item.state === "available"))),
    staleTime: 30_000,
  });
  useEffect(() => {
    if (detail.data || orientation.data) trimSignalWindowCache(client, props.dataSource.key);
  }, [client, detail.data, orientation.data, props.dataSource.key]);

  function updateViewerState(action: () => void) {
    action();
  }
  function seek(seconds: number) {
    setHoverSeconds(null);
    setScrubberPreviewSeconds(null);
    updateViewerState(() => setSelectedSeconds(clampSelectedTime(seconds, props.dataSource.durationSeconds)));
  }
  function setPreference<Name extends keyof ViewerPreferences>(name: Name, value: ViewerPreferences[Name]) {
    setPreferences((current) => ({ ...current, [name]: value }));
  }
  function previewAtPointer(event: ReactPointerEvent<HTMLDivElement>) {
    if (!preferences.cursorFollowsMouse) return;
    if (!Number.isFinite(event.clientX)) return;
    const target = event.target instanceof Element ? event.target : null;
    const plot = target?.closest<HTMLElement>(".signal-track-plot");
    if (!plot || !event.currentTarget.contains(plot)) {
      setHoverSeconds(null);
      return;
    }
    const bounds = plot.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || bounds.width <= 0) {
      setHoverSeconds(null);
      return;
    }
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const preview = interval.start_seconds + ratio * (interval.end_seconds - interval.start_seconds);
    if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
    hoverFrame.current = window.requestAnimationFrame(() => {
      setHoverSeconds(preview);
      hoverFrame.current = null;
    });
  }
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const key = event.key;
      if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowDown") return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || hasNativeArrowKeyBehavior(event.target)) return;
      event.preventDefault();
      const next = applyViewerArrowKey(
        { selectedSeconds, timeWindow },
        key,
        props.dataSource.durationSeconds,
      );
      updateViewerState(() => {
        setSelectedSeconds(next.selectedSeconds);
        setTimeWindow(next.timeWindow);
      });
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [client, identity, selectedSeconds, timeWindow, props.dataSource.durationSeconds]);

  function changeCameraView(source: "head" | "torso", cameraView: CameraView) {
    lastCameraSource.current = source;
    setCameraViews((current) => updateCameraViews(current, source, cameraView, preferences.link3dViews));
  }
  const displayed = timeWindow === "full" ? overview.data : detail.data;
  const interval = displayed?.interval || (timeWindow === "full"
    ? { start_seconds: 0, end_seconds: props.dataSource.durationSeconds }
    : { start_seconds: windowStart, end_seconds: Math.min(props.dataSource.durationSeconds, windowStart + duration) });
  const capabilityById = new Map((capabilities.data?.modalities || []).map((item) => [item.id, item]));
  const modalities = props.dataSource.profile.modalities
    .filter((item) => item.kind !== "quaternion" && item.kind !== "media")
    .filter((item) => capabilityById.get(item.id)?.state === "available" || item.fallback === "show_status")
    .filter((item) => layer === "both" || item.origin === layer)
    .sort((left, right) => left.order - right.order);
  const series = displayed?.series || [];
  const displayedSeconds = scrubberPreviewSeconds ?? hoverSeconds ?? selectedSeconds;
  const cursorRatio = Math.max(0, Math.min(1, (displayedSeconds - interval.start_seconds) /
    Math.max(1e-9, interval.end_seconds - interval.start_seconds)));
  const trackRows = modalities.flatMap((modality) => {
    const capability = capabilityById.get(modality.id);
    const modalitySeries = series.filter((item) => item.modality_id === modality.id);
    if (!preferences.separateMultichannelSignals || modality.kind !== "multichannel" || !capability || capability.channels.length <= 1) {
      return [{ key: modality.id, label: modality.label, modality, capability, series: modalitySeries }];
    }
    return capability.channels.map((channel, index) => ({
      key: `${modality.id}:${channel.id}`,
      label: separatedChannelLabel(modality.label, channel.label, index),
      modality,
      capability: { ...capability, channels: [channel] },
      series: modalitySeries.filter((item) => item.channel_id === channel.id || item.channel_label === channel.label),
    }));
  });
  const orientationByModel = new Map<string, OrientationSelection>();
  for (const config of ORIENTATION_MODELS) {
    const modality = orientationModalities.find((item) => item.id === config.modalityId);
    const orientationSeconds = scrubberPreviewSeconds == null ? displayedSeconds : selectedSeconds;
    if (orientation.data && modality) orientationByModel.set(config.id, orientationAtTime(orientation.data, modality, orientationSeconds, config.calibrationWxyz));
  }
  const availableCount = capabilities.data?.modalities.filter((item) => item.state === "available").length || 0;
  const unavailableCount = (capabilities.data?.modalities.length || 0) - availableCount;

  return (
    <article className="data-viewer-shell">
      {props.summary}
      {props.provenance}
      <section className="viewer-controls" aria-label="Signal viewer controls">
        {props.toolbar}
        <fieldset><legend>Layer</legend><div className="layer-toggle">{(["both", "recorded", "derived"] as const).map((item) =>
          <button aria-pressed={layer === item} key={item} onClick={() => updateViewerState(() => setLayer(item))} type="button">{item}</button>)}</div></fieldset>
        <label><span>Detail window</span><select value={timeWindow} onChange={(event) => updateViewerState(() => setTimeWindow(event.target.value === "full" ? "full" : Number(event.target.value) as 15 | 30 | 60))}>
          <option value="full">Full night</option><option value={15}>15 seconds</option><option value={30}>30 seconds</option><option value={60}>60 seconds</option>
        </select></label>
        <button className="reset-view" onClick={() => updateViewerState(() => { setSelectedSeconds(0); setTimeWindow(INITIAL_TIME_WINDOW); })} type="button">Reset view</button>
        <details className="view-settings" ref={settingsMenu} onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          if (settingsMenu.current) settingsMenu.current.open = false;
          settingsMenu.current?.querySelector<HTMLElement>("summary")?.focus();
        }}>
          <summary><span aria-hidden="true">⚙</span> View settings</summary>
          <div className="view-settings-popover">
            <fieldset><legend>Channels</legend>
              <label><input checked={preferences.separateMultichannelSignals} onChange={(event) => setPreference("separateMultichannelSignals", event.target.checked)} type="checkbox" /> Separate multichannel signals</label>
            </fieldset>
            <fieldset><legend>Display</legend>
              <label><input checked={preferences.showSelectedTotalTime} onChange={(event) => setPreference("showSelectedTotalTime", event.target.checked)} type="checkbox" /> Show selected / total time</label>
              <label><input checked={preferences.alignTimelineToSignalViewport} onChange={(event) => setPreference("alignTimelineToSignalViewport", event.target.checked)} type="checkbox" /> Align timeline to signal viewport</label>
              <label><input checked={preferences.highlightActiveSleepStage} onChange={(event) => setPreference("highlightActiveSleepStage", event.target.checked)} type="checkbox" /> Highlight active sleep stage</label>
            </fieldset>
            <fieldset><legend>Interaction</legend>
              <label><input checked={preferences.cursorFollowsMouse} onChange={(event) => setPreference("cursorFollowsMouse", event.target.checked)} type="checkbox" /> Cursor follows mouse</label>
              <label><input checked={preferences.link3dViews} onChange={(event) => {
                const linked = event.target.checked;
                setPreference("link3dViews", linked);
                if (linked) setCameraViews((current) => linkCameraViews(current, lastCameraSource.current));
              }} type="checkbox" /> Link 3D views</label>
            </fieldset>
          </div>
        </details>
      </section>
      <div className="viewer-transport">
        <div className={`signal-transport${preferences.alignTimelineToSignalViewport ? " timeline-aligned" : ""}`}>
          <div className="viewer-transport-meta">
            {preferences.showSelectedTotalTime && <output aria-label="Selected time / recording duration">
              {formatRecordingTime(displayedSeconds, true)} <span>/ {formatRecordingTime(props.dataSource.durationSeconds, true)}</span>
            </output>}
            <span className="keyboard-hint">← → seek · ↑ ↓ window</span>
          </div>
          <FullNightTimeline
            detailDuration={timeWindow === "full" ? null : duration}
            detailStart={windowStart}
            duration={props.dataSource.durationSeconds}
            onCommit={seek}
            onPreview={(seconds) => setScrubberPreviewSeconds(seconds == null ? null : clampSelectedTime(seconds, props.dataSource.durationSeconds))}
            selected={scrubberPreviewSeconds ?? selectedSeconds}
          />
        </div>
        <span className="manual-sync-state">Manual selected time</span>
      </div>
      {props.attention && <Alert className="viewer-attention">{props.attention}</Alert>}
      {capabilities.isPending && <p className="viewer-loading"><Spinner /> Resolving pinned signal capabilities…</p>}
      {capabilities.isError && <Alert className="viewer-error">The recording capabilities could not be loaded. <button onClick={() => void capabilities.refetch()} type="button">Retry</button></Alert>}
      {capabilities.data && (timeWindow === "full" ? overview.isError : detail.isError) && <Alert className="viewer-error">The requested signal view is temporarily unavailable. <button onClick={() => void (timeWindow === "full" ? overview.refetch() : detail.refetch())} type="button">Retry</button></Alert>}
      {capabilities.data && (
        <div className="viewer-grid">
          <section className="signal-table" aria-label="Synchronized signal tracks">
            <div className="signal-tracks" data-cursor-preview={hoverSeconds != null ? "true" : "false"} onPointerLeave={() => {
              if (hoverFrame.current != null) window.cancelAnimationFrame(hoverFrame.current);
              hoverFrame.current = null;
              setHoverSeconds(null);
            }} onPointerMove={previewAtPointer}>
              <TimeAxis interval={interval} />
              {trackRows.map((row) => <TrackRow
                capability={row.capability} highlightActiveStage={preferences.highlightActiveSleepStage} interval={interval} key={row.key}
                label={row.label} loading={timeWindow === "full" ? overview.isPending : detail.isPending} modality={row.modality}
                onSeek={seek} requestError={timeWindow === "full" ? overview.isError : detail.isError}
                selectedSeconds={displayedSeconds} series={row.series}
              />)}
              <i aria-hidden="true" className="shared-track-cursor" data-testid="shared-track-cursor" style={{ left: `calc(var(--track-label-width) + (100% - var(--track-label-width)) * ${cursorRatio})` }} />
            </div>
            <footer className="signal-table-footer"><i /><span>Manual synchronized view · native recording time</span></footer>
          </section>
          <aside className="viewer-auxiliary">
            <div className="orientation-controls" aria-label="Orientation view controls">
              <button onClick={() => setCameraViews(createDefaultCameraViews())} type="button">Reset camera</button>
            </div>
            {ORIENTATION_MODELS.map((config) => <OrientationPanel
              cameraView={cameraViews[config.id]}
              config={config}
              key={config.id}
              onCameraViewChange={(cameraView) => changeCameraView(config.id, cameraView)}
              selection={orientationByModel.get(config.id) || null}
            />)}
          </aside>
        </div>
      )}
      <footer className="viewer-quality-footer">
        <span>{props.qualityLabel || "Recording quality"} · {availableCount} available · {unavailableCount} unavailable</span>
        <span>Selected time synchronizes signals, Sleep stage, head and torso</span>
      </footer>
    </article>
  );
}
